'use client'

import { useSearchParams } from 'next/navigation'
import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { SORT_BY_PERTINENCE, SortByChoice } from '../FilterProductFormMobile/OrderProducts'

type FormStateKey = 'colors' | 'collections' | 'types'

export type ProductFilterFormState = {
  colors: number[]
  collections: number[]
  types: number[]
  priceRange: {
    min?: number
    max?: number
  }
  availableOnline: boolean
  sortBy: SortByChoice
}

const defaultFormState: ProductFilterFormState = {
  colors: [],
  collections: [],
  types: [],
  priceRange: {},
  availableOnline: false,
  sortBy: SORT_BY_PERTINENCE,
}

const mapStateToQueryParams = (formState: ProductFilterFormState) => {
  const params = {
    colors: formState.colors,
    types: formState.types,
    collections: formState.collections,
    min: formState.priceRange.min ? formState.priceRange.min : undefined,
    max: formState.priceRange.max ? formState.priceRange.max : undefined,
    availableOnline: formState.availableOnline ? true : undefined,
    sortBy: formState.sortBy !== SORT_BY_PERTINENCE ? formState.sortBy : undefined,
  }

  return params
}

const buildUrlSearchParams = (params: ReturnType<typeof mapStateToQueryParams>) => {
  const queryParams = new URLSearchParams()

  if (params.colors.length) queryParams.set('colors', params.colors.join())
  if (params.types.length) queryParams.set('types', params.types.join())
  if (params.collections.length) queryParams.set('collections', params.collections.join())
  if (params.min) queryParams.set('min', params.min.toString())
  if (params.max) queryParams.set('max', params.max.toString())
  if (params.availableOnline) queryParams.set('availableOnline', 'true')
  if (params.sortBy) queryParams.set('sortBy', params.sortBy.toString())

  return queryParams
}

const splitListOfNumbers = (value: string | string[] | undefined) => {
  if (typeof value !== 'string') {
    return []
  }

  return value.split(',').map((v) => Number(v))
}

const mapQueryParamsToState = (queryParams: any) => {
  const formState: ProductFilterFormState = {
    ...defaultFormState,
  }

  formState.colors = splitListOfNumbers(queryParams.get('colors'))
  formState.collections = splitListOfNumbers(queryParams.get('collections'))
  formState.types = splitListOfNumbers(queryParams.get('types'))

  if (queryParams.get('min') && queryParams.get('max')) {
    formState.priceRange = {
      min: Number(queryParams.get('min')),
      max: Number(queryParams.get('max')),
    }
  }
  formState.availableOnline = !!queryParams.get('availableOnline')
  formState.sortBy = queryParams.get('sortBy')
    ? (Number(queryParams.get('sortBy')) as SortByChoice)
    : SORT_BY_PERTINENCE

  return formState
}

// Custom Hook
const useProductFilterForm = () => {
  const [formState, setFormState] = useState(mapQueryParamsToState(useSearchParams()))

  useEffect(() => {
    updateRoute(formState)
  }, [formState])

  const updateRoute = useCallback((newState: ProductFilterFormState) => {
    const params = mapStateToQueryParams(newState)
    const queryParams = buildUrlSearchParams(params)
    window.history.replaceState(
      window.history.state,
      '',
      queryParams.toString().length !== 0
        ? `?${decodeURIComponent(queryParams.toString())}`
        : window.location.href.replace(window.location.search, ''),
    )
  }, [])

  const onChangeFilter = (nameFilter: FormStateKey, value: number) => {
    const selectedFields = formState[nameFilter]

    // Supprimer
    if (selectedFields.includes(value)) {
      const newSelectedFields = selectedFields.filter((field) => field !== value)

      // Met a jour le formState et merge 2 array
      setFormState((prevFormState) => {
        const newState = {
          ...prevFormState,
          [nameFilter]: newSelectedFields,
        }
        return newState
      })

      return
    }

    // Ajouter
    const newSelectedFields = [...selectedFields, value]
    setFormState((prevFormState) => {
      const newState = {
        ...prevFormState,
        [nameFilter]: newSelectedFields,
      }
      return newState
    })
  }

  const onChangePriceFilter = (value: { min?: number; max?: number }) => {
    setFormState((prevFormState) => {
      const newState = {
        ...prevFormState,
        priceRange: value,
      }
      return newState
    })
  }

  const onChangeAvailableOnline = () => {
    setFormState((prevFormState) => {
      const newState = {
        ...prevFormState,
        availableOnline: !formState.availableOnline,
      }
      return newState
    })
  }

  const onChangeSort = (value: SortByChoice) => {
    setFormState((prevFormState) => {
      const newState = {
        ...prevFormState,
        sortBy: value,
      }
      return newState
    })
  }

  const resetFilterForm = () => {
    setFormState(defaultFormState)
  }

  return {
    onChangeFilter,
    onChangePriceFilter,
    onChangeAvailableOnline,
    onChangeSort,
    resetFilterForm,
    formState,
  }
}

type ProductFilterFormContextType = {
  formState: ProductFilterFormState

  onChangeFilter: (nameFilter: FormStateKey, value: number) => void

  onChangePriceFilter: (value: { min?: number; max?: number }) => void

  onChangeAvailableOnline: () => void

  onChangeSort: (value: SortByChoice) => void
  resetFilterForm: () => void
}

export const ProductFilterFormContext = createContext<ProductFilterFormContextType>({
  formState: defaultFormState,
  onChangeFilter: () => {},
  onChangePriceFilter: () => {},
  onChangeAvailableOnline: () => {},
  onChangeSort: () => {},
  resetFilterForm: () => {},
})

export const ProductFilterFormContextProvider = ({ children }: { children: ReactNode }) => {
  // Custom Hook
  const {
    onChangeFilter,
    onChangePriceFilter,
    onChangeAvailableOnline,
    onChangeSort,
    resetFilterForm,
    formState,
  } = useProductFilterForm()

  const context = useMemo(
    () => ({
      onChangeFilter,
      onChangePriceFilter,
      onChangeAvailableOnline,
      onChangeSort,
      resetFilterForm,
      formState,
    }),
    [
      onChangeFilter,
      onChangePriceFilter,
      onChangeAvailableOnline,
      onChangeSort,
      resetFilterForm,
      formState,
    ],
  )

  return (
    <ProductFilterFormContext.Provider value={context}>
      {children}
    </ProductFilterFormContext.Provider>
  )
}
